import { rootReducer } from "./reducer";
import { DateTime } from "luxon";
import {
  configureStore,
  Middleware,
  UnknownAction,
  ThunkAction,
} from "@reduxjs/toolkit";
import * as redux from "react-redux";
import { reportInfo } from "./helpers/error-helper";
import Decimal from "decimal.js-light";
import * as Sentry from "@sentry/react";
import { listenerMiddleware } from "./listener-middleware";
import { startListeners } from "./listeners";

/*
const logLocal =
    (_store: RootStore) => (next: AppDispatch) => (action: AnyAction) => {
        // const preState = store.getState();
        const result = next(action);
        // const postState = store.getState();
        console.log({ action });
        return result;
    };
*/

// eslint-disable-next-line
const logToServer =
  (_store: RootStore) => (next: AppDispatch) => (action: UnknownAction) => {
    // const preState = store.getState();
    const result = next(action);
    // const postState = store.getState();
    reportInfo({ action });
    return result;
  };

// Records each dispatched action's type (never its payload or the state) as a
// Sentry breadcrumb, so an error event shows which actions ran just before it.
// Unlike the redux enhancer below, this stays far from Sentry's size limit.
const sentryActionBreadcrumbs: Middleware =
  () => (next) => (action) => {
    const type = (action as { type?: unknown } | null)?.type;
    if (typeof type === "string") {
      Sentry.addBreadcrumb({ category: "redux.action", message: type });
    }
    return next(action);
  };

/*
Caused issues because sentry has a strict 1mb limit on data that can be sent.
const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  stateTransformer: (state) => {
    // Only send minimal state to avoid exceeding Sentry size limits
    return {
      auth: state.auth?.status,
      routeCount: state.routes?.routeIds?.length,
      cargoCount: Object.keys(state.cargo?.cargoEntities || {}).length,
    };
  },
  actionTransformer: (action) => {
    // Only send minimal action to avoid exceeding Sentry size limits
    return {
      type: action.type,
    };
  },
});
*/

export const store = configureStore({
  reducer: rootReducer,
  // Before `enhancers`: configureStore infers the middleware tuple from
  // whichever of the two callbacks comes first.
  middleware: (getDefault) =>
    getDefault({ serializableCheck: false })
      // Prepended, per RTK docs: listeners must see actions before any
      // middleware that could swallow them.
      .prepend(listenerMiddleware.middleware)
      .concat(sentryActionBreadcrumbs),
  // getDefault().prepend(logToServer as any) as any,
  enhancers: (getDefaultEnhancers) =>
    getDefaultEnhancers() /*.concat(sentryReduxEnhancer)*/,
  devTools: process.env.NODE_ENV !== "production" && {
    serialize: {
      replacer: (_key, value) => {
        if (value instanceof Decimal) {
          return { $type: "Decimal", value: value.toString() };
        }
        if (value instanceof DateTime) {
          return { $type: "DateTime", value: value.toISO() };
        }
        return value;
      },
      reviver: (_key, value: any) => {
        if (typeof value === "object" && value !== null && "$type" in value) {
          switch (value.$type) {
            case "Decimal":
              return new Decimal(value.data);
            case "DateTime":
              return DateTime.fromISO(value.data, { setZone: true });
          }
        }
      },
    },
  },
});

startListeners();

export type AppThunkAction<T = void> = ThunkAction<
  T,
  RootState,
  any,
  UnknownAction
>;
export type AsyncAppThunkAction<T = void> = AppThunkAction<Promise<T>>;
export type RootState = ReturnType<typeof rootReducer>;
export type RootStore = typeof store;
export type AppDispatch = RootStore["dispatch"];
export type GetState = RootStore["getState"];
export type Action = Parameters<AppDispatch>[0];
export type ActionCreator<T extends any[]> = (...args: T) => any;
export const useAppDispatch = () => redux.useDispatch<AppDispatch>();
export const useSelector = <T>(selector: (state: RootState) => T) =>
  redux.useSelector(selector);
